/**
 * Load admin-API data from the browser.
 *
 * Every page in this app used to be a server component: it awaited its data
 * before rendering, so there was no in-between state to design for — the page
 * either arrived complete or the request failed. A static SPA has no server to
 * do that, so the same page now renders first and fetches second, and the
 * in-between is real. This hook is the one place that shape lives, so 36 ported
 * pages describe it the same way instead of each inventing a loading flag.
 *
 * Three behaviours worth knowing:
 *
 *  * A page unmounted mid-flight must not set state, and a second load must not
 *    be overtaken by the first. One cancel flag per run covers both: React runs
 *    the cleanup before the next effect, so a superseded request can no longer
 *    settle. Without that, switching sports twice quickly leaves the wrong
 *    sport's rows on screen, which looks like a data bug and is not one.
 *  * Nothing is set synchronously inside the effect, and no ref is written
 *    during render. Both cost a render pass, and neither is needed: `loading`
 *    is derived, and the effect closes over the `load` from the render that
 *    scheduled it — which is the one whose `deps` it belongs to.
 */

import { useCallback, useEffect, useState } from "react";

import { ApiError } from "@/lib/api/client";
import { REVALIDATE_EVENT } from "@/lib/revalidate";

/** Nothing has come back yet / it worked / it did not. */
type Settled<T> =
  | { status: "pending" }
  | { status: "ok"; data: T }
  | { status: "failed"; error: string; cause: unknown };

export type ApiState<T> = {
  /** The loaded value, or undefined until the first load succeeds. */
  data: T | undefined;
  /**
   * True until the first result lands — not "a request is in flight".
   *
   * A reload keeps the previous rows on screen rather than blanking them, so
   * after the first result this stays false. Pages use it for their initial
   * placeholder, which is the only place the distinction shows.
   */
  loading: boolean;
  /** The failure message, already unwrapped from ApiError. */
  error: string | undefined;
  /** What was actually thrown. Pages that treat a 404 differently from an
   *  outage need the status, and a message string cannot carry it. */
  cause: unknown;
  /** Run the loader again — for retry buttons and post-mutation refreshes. */
  reload: () => void;
};

/** Turn whatever was thrown into something worth putting on screen. */
export function messageFor(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** Like `messageFor`, but lists each field problem when the server sent them. */
export function errorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.violations.length > 0) return error.violations.join("; ");
  return messageFor(error, fallback);
}

/**
 * Call `load` on mount and whenever `deps` change.
 *
 * `load` is intentionally not in the dependency list: pages write it as an
 * inline arrow, which is a new function every render, and depending on it would
 * refetch forever. `deps` is what the caller says actually changes.
 */
export function useApi<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
  fallbackMessage = "failed to load",
): ApiState<T> {
  const [settled, setSettled] = useState<Settled<T>>({ status: "pending" });
  /** Bumped by `reload` to re-run the effect without changing `deps`. */
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    load().then(
      (data) => {
        if (!cancelled) setSettled({ status: "ok", data });
      },
      (thrown: unknown) => {
        if (cancelled) return;
        setSettled({
          status: "failed",
          error: messageFor(thrown, fallbackMessage),
          cause: thrown,
        });
      },
    );
    return () => {
      cancelled = true;
    };
    // `deps` is the caller's own list; `nonce` is what `reload` moves. `load`
    // and `fallbackMessage` are captured deliberately — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  // A write elsewhere in the app said the data behind some path changed. In
  // Next this was `revalidatePath` invalidating a server cache; here it is the
  // only thing standing between "created the record" and "the list still does
  // not show it". See lib/revalidate.ts.
  useEffect(() => {
    window.addEventListener(REVALIDATE_EVENT, reload);
    return () => window.removeEventListener(REVALIDATE_EVENT, reload);
  }, [reload]);

  return {
    data: settled.status === "ok" ? settled.data : undefined,
    loading: settled.status === "pending",
    error: settled.status === "failed" ? settled.error : undefined,
    cause: settled.status === "failed" ? settled.cause : undefined,
    reload,
  };
}
